import React, { useState } from 'react';
import logo from './asset/logo.png'
import { Package, Zap, Globe, Wifi, Clock, Mail, Menu, X, CheckCircle, Monitor, AlertCircle, Shield } from 'lucide-react';

export default function ShippingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <img
                    src={logo} // replace with your image path
                    alt="Description"
                    width="100px"
                  />
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Home
              </a>
            
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200">
              <a href="/" className="block text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Home
              </a>
             
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 opacity-90"></div>
        <div className="relative py-16 px-4 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Zap className="w-5 h-5" />
              <span className="text-sm font-semibold">100% Digital Delivery</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Shipping & Delivery Policy
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Instant digital access - No physical shipping required
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-white/80">
              <Clock className="w-4 h-4" />
              <span>Last updated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
      </div>

      {/* Important Notice */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 mb-8 relative z-10">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl shadow-2xl p-6 text-white border-2 border-emerald-300">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Monitor className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Digital Services Only</h3>
              <p className="text-white/95 leading-relaxed">
                IEEEAssistant.in provides <strong>digital services only</strong>. We do not ship, 
                courier or deliver any physical goods. All products are delivered electronically 
                through our website and platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="max-w-4xl mx-auto px-4 mb-8">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-emerald-100 hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Instant Access</h3>
            <p className="text-sm text-gray-600">Immediate digital delivery after payment</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-teal-100 hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Globe className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Global Access</h3>
            <p className="text-sm text-gray-600">Available worldwide, 24/7</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-cyan-100 hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6 text-cyan-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">No Shipping</h3>
            <p className="text-sm text-gray-600">Zero physical delivery</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        {/* Digital Delivery */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Digital Delivery</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    Once your payment is successfully processed, access to the purchased
                    plan or subscription features is granted <strong>immediately</strong> through your
                    registered account.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    You will also receive an email confirmation with transaction
                    details. No physical invoice or shipment is sent.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    In case of technical delays or payment confirmation issues, service
                    access may take up to <strong>24 hours</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Availability */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Monitor className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Service Availability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our services are hosted online and can be accessed 24x7 (subject to
                scheduled maintenance or unforeseen downtime). Any planned downtime
                will be notified in advance on the platform.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">24/7 Access</span>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Cloud Hosted</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* International Users */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. International Users</h2>
              <p className="text-gray-700 leading-relaxed">
                IEEEAssistant.in is accessible globally. However, taxes, billing and
                currency conversion depend on your location and payment method. No
                physical shipping is involved in any case.
              </p>
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-900">Available Worldwide</span>
                </div>
                <p className="text-sm text-purple-800">
                  Access from anywhere with an internet connection
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Responsibility */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wifi className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Customer Responsibility</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    You are responsible for ensuring you have a stable internet
                    connection and compatible device/browser to access the services.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Mail className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    If you experience issues accessing your plan after payment, please
                    contact <a href="mailto:support@ieeeassistant.in" className="text-emerald-600 hover:underline font-semibold">support@ieeeassistant.in</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Compliance */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl shadow-xl p-8 mb-6 border-2 border-emerald-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Legal Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                This policy is published in compliance with the Consumer Protection
                (E-Commerce) Rules, 2020. Since our services are digital in nature,
                there is <strong>no physical shipment</strong>. Delivery is deemed
                complete when access is granted to the customer's account.
              </p>
              <div className="flex items-center gap-2 p-3 bg-emerald-100 rounded-lg border border-emerald-300">
                <CheckCircle className="w-5 h-5 text-emerald-700" />
                <span className="text-sm font-semibold text-emerald-900">
                  Compliant with Consumer Protection (E-Commerce) Rules, 2020
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">How Digital Delivery Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Purchase Plan</h3>
              <p className="text-sm text-gray-600">
                Choose your subscription and complete secure payment
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">2</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Instant Activation</h3>
              <p className="text-sm text-gray-600">
                Features unlock immediately in your account
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-600">3</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Start Using</h3>
              <p className="text-sm text-gray-600">
                Access all features from any device, anywhere
              </p>
            </div>
          </div>
        </div>

        {/* Contact Card */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Need Help with Access?</h3>
            <p className="mb-6 text-white/90">
              Our support team is here to assist you with any delivery or access issues
            </p>
            <a
              href="mailto:support@ieeeassistant.in"
              className="inline-block bg-white text-emerald-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              support@ieeeassistant.in
            </a>
            <p className="mt-4 text-sm text-white/80">
              Response time: Within 24 hours
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 IEEEAssistant.in. All rights reserved. | 100% Digital Service
          </p>
        </div>
      </footer>
    </div>
  );
}